/**
 * Keg → pour price suggestions (all pour prices inc GST).
 * Divisor = 1 − targetGpPct/100 when a Guide target exists; else hardcoded fallback.
 * Venue/L (tap fill) floors to 50c; Pint skips ABV over PINT_MAX_ABV (legal 8%).
 */

import {
  kegVolumeMlFromSku,
  resolveTargetEntry,
  resolveTargetPct,
} from './marginUtils';

/** NZ legal pint cap. Not a Guide rule — will not change. */
export const PINT_MAX_ABV = 8;

/** Pour columns filled from keg cascade / bulk autofill */
export const KEG_POUR_FIELDS = [
  'priceVenuePerLitre',
  'pricePint',
  'priceHalfPint',
  'price150ml',
];

/** Historic Monday divisors — used when no Guide target is set. */
export const KEG_FALLBACK_DIVISORS = {
  priceVenuePerLitre: 0.7,
  pricePint: 0.28,
  priceHalfPint: 0.28,
  price150ml: 0.25,
};

/**
 * Implied GP% from fallback divisors (1 − d) × 100.
 * Venue/L is 30% (d=0.7). Pint/Half use Monday 0.28 → 72%.
 */
export const IMPLIED_KEG_TARGET_PCT = {
  price_venue_per_litre: 30,
  price_pint: 72,
  price_half_pint: 72,
  price_150ml: 75,
};

export const KEG_POUR_VOLUME_L = {
  pricePint: 0.44,
  priceHalfPint: 0.285,
  price150ml: 0.15,
};

const UPLIFT_POUR_FIELDS = new Set([
  'priceVenuePerLitre',
  'pricePint',
  'priceHalfPint',
  'price150ml',
]);

/** ROUND(x * 2, 0) / 2 — nearest $0.50 */
export function roundToNearest50c(value) {
  if (value == null || Number.isNaN(value)) return null;
  return Math.round(value * 2) / 2;
}

export function roundCeil50c(value) {
  if (value == null || Number.isNaN(value)) return null;
  return Math.ceil(value * 2) / 2;
}

export function roundFloor50c(value) {
  if (value == null || Number.isNaN(value)) return null;
  return Math.floor(value * 2) / 2;
}

export function roundNearestCent(value) {
  if (value == null || Number.isNaN(value)) return null;
  return Math.round(value * 100) / 100;
}

export function applyRounding(value, mode) {
  if (mode === 'ceil_50') return roundCeil50c(value);
  if (mode === 'floor_50') return roundFloor50c(value);
  if (mode === 'nearest_cent') return roundNearestCent(value);
  return roundToNearest50c(value);
}

/** Parse ABV from product string (e.g. "5.5%", "8.5"). */
export function parseAbv(abvRaw) {
  if (abvRaw == null || abvRaw === '') return null;
  const n = parseFloat(String(abvRaw).replace(/%/g, '').trim());
  return Number.isNaN(n) ? null : n;
}

/** cost/sell ratio from a Guide target GP%. Null if missing or invalid. */
export function targetPctToDivisor(pct) {
  const n = parseFloat(pct);
  if (Number.isNaN(n) || n < 0 || n >= 100) return null;
  return 1 - n / 100;
}

export function isHighAbvOrWild(abv, productType, rules) {
  const abvNum = parseAbv(abv);
  const threshold = parseFloat(rules?.pour_uplift_abv ?? 10);
  const highAbv = abvNum != null && !Number.isNaN(threshold) && abvNum >= threshold;
  const isWild = String(productType || '') === 'Wild';
  return highAbv || isWild;
}

function divisorFor(field, targetPcts, fallback) {
  const fromTarget = targetPctToDivisor(targetPcts?.[field]);
  if (fromTarget != null && fromTarget > 0) return fromTarget;
  return fallback != null ? fallback : KEG_FALLBACK_DIVISORS[field];
}

function roundingFor(field, roundingMap) {
  if (roundingMap?.[field]) return roundingMap[field];
  if (field === 'priceVenuePerLitre') return 'floor_50';
  return 'nearest_50';
}

/**
 * @param {{
 *   priceOn: string|number,
 *   kegVolumeMl?: number,
 *   abv?: string|null,
 *   targetPcts?: Record<string, number|null>,
 *   roundingMap?: Record<string, string>,
 * }} args
 */
export function calculateKegSuggestedPrices({
  priceOn, kegVolumeMl, abv, targetPcts, roundingMap,
}) {
  const priceOnNum = parseFloat(String(priceOn));
  if (Number.isNaN(priceOnNum) || priceOnNum <= 0) return null;
  const volMl = kegVolumeMl != null ? kegVolumeMl : 50000;
  const volL = volMl / 1000;
  if (volL <= 0) return null;
  const kegPL = priceOnNum / volL;
  const abvNum = parseAbv(abv);

  const GST = 1.15;

  const litreRaw = (kegPL / divisorFor('priceVenuePerLitre', targetPcts)) * GST;
  const priceVenuePerLitre = applyRounding(
    litreRaw, roundingFor('priceVenuePerLitre', roundingMap),
  );

  let pricePint = null;
  const skipPint = abvNum != null && abvNum > PINT_MAX_ABV;
  if (!skipPint) {
    const pintRaw = ((kegPL * KEG_POUR_VOLUME_L.pricePint)
      / divisorFor('pricePint', targetPcts)) * GST;
    pricePint = applyRounding(pintRaw, roundingFor('pricePint', roundingMap));
  }

  const halfRaw = ((kegPL * KEG_POUR_VOLUME_L.priceHalfPint)
    / divisorFor('priceHalfPint', targetPcts)) * GST;
  const priceHalfPint = applyRounding(
    halfRaw, roundingFor('priceHalfPint', roundingMap),
  );

  const pour150Raw = ((kegPL * KEG_POUR_VOLUME_L.price150ml)
    / divisorFor('price150ml', targetPcts)) * GST;
  const price150ml = applyRounding(
    pour150Raw, roundingFor('price150ml', roundingMap),
  );

  return {
    priceVenuePerLitre,
    pricePint,
    priceHalfPint,
    price150ml,
  };
}

function skuProductRange(sku) {
  if (!sku) return null;
  return sku.productRange ?? sku.product_range ?? null;
}

/**
 * @param {object} sku
 * @param {string|number|undefined|null} priceOn
 * @param {string|null|undefined} productAbv
 * @param {object} [targetMap]
 * @param {{ productType?: string, rules?: object }} [opts]
 */
export function getKegSuggestionsForSku(sku, priceOn, productAbv, targetMap, opts) {
  if (!sku) return null;
  const range = skuProductRange(sku);
  const productType = opts?.productType ?? sku.productType ?? sku.product_type;
  const rules = opts?.rules;
  const high = isHighAbvOrWild(productAbv, productType, rules);
  const targetPcts = {};
  const roundingMap = {};
  if (targetMap) {
    KEG_POUR_FIELDS.forEach((field) => {
      const cond = high && UPLIFT_POUR_FIELDS.has(field) ? 'high_abv_or_wild' : '';
      const entry = resolveTargetEntry(targetMap, range, field, cond);
      const pct = entry?.pct ?? resolveTargetPct(targetMap, range, field, cond);
      if (pct != null) targetPcts[field] = pct;
      if (entry?.rounding) roundingMap[field] = entry.rounding;
    });
  }
  return calculateKegSuggestedPrices({
    priceOn,
    kegVolumeMl: kegVolumeMlFromSku(sku),
    abv: productAbv,
    targetPcts,
    roundingMap,
  });
}

/** Compare proposed string to suggested number (50c rounding). */
export function valueMatchesSuggestion(proposedStr, suggestedNum) {
  if (suggestedNum == null || proposedStr == null || proposedStr === '') return false;
  const a = parseFloat(String(proposedStr));
  const b = suggestedNum;
  if (Number.isNaN(a)) return false;
  return Math.abs(a - b) < 0.001;
}
